/**
 * EffectiveSLP — kid-facing practice player child identifier.
 *
 * Privacy mechanism: the parent picks a nickname ("Otter", "Robin",
 * etc.) on the welcome screen and we store a salted SHA-256 of
 * (tokenId || ':' || nickname) as the `child_identifier_hash`. The
 * server NEVER sees the raw nickname — it just receives the hash
 * and recomputes it on every attempt submission.
 *
 * Formula MUST agree byte-for-byte with the server-side helper at
 * `supabase/functions/_shared/childIdentifierHash.ts`. Both:
 *   1. UTF-8 encode `tokenId + ':' + nickname`
 *   2. SHA-256 the bytes via Web Crypto
 *   3. Hex-encode lowercase with no separators
 *
 * Lives in its own module so the .tsx files can stay pure
 * components (satisfies the `react/only-export-components` oxlint
 * rule the rest of web/src follows).
 */

/**
 * Compute the per-token salted hash of a child nickname.
 *
 * Synchronous-text wrapper around `crypto.subtle.digest('SHA-256', ...)`
 * that returns the lowercase-hex digest. Async because SubtleCrypto
 * is async across browsers.
 *
 * Formula: `SHA-256(tokenId || ':' || nickname)` — hex-encoded,
 * no separators. Same formula on the server (see
 * `supabase/functions/_shared/childIdentifierHash.ts`).
 *
 * Throws if the browser does not expose `crypto.subtle` — the
 * welcome-screen submit handler should feature-detect and surface
 * a friendly error. All current EffectiveSLP target browsers
 * (Safari 15+, Chrome 100+, Firefox 100+) support it.
 */
export async function computeChildIdentifierHash(
  tokenId: string,
  nickname: string,
): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto SubtleCrypto API unavailable');
  }
  const bytes = new TextEncoder().encode(`${tokenId}:${nickname}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToHex(new Uint8Array(digest));
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i] ?? 0;
    out += b.toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * Friendly nicknames suggested on the welcome screen. Fixed list
 * so the same nickname ("Otter") is easy to remember across
 * siblings and devices.
 */
export const FRIENDLY_ANIMAL_NICKNAMES: readonly string[] = [
  'Otter',
  'Robin',
  'Panda',
  'Fox',
  'Bear',
  'Owl',
] as const;

/**
 * Storage key for the welcome-screen nickname. Keyed by `token_id`
 * so each practice link gets its own slot. Survives reloads on
 * the same device; cross-device is intentionally broken (privacy).
 */
export function nicknameStorageKey(tokenId: string): string {
  return `practice:${tokenId}:nickname`;
}

/**
 * Default nickname suggestion for the welcome-screen input. Picks
 * the first animal from the fixed list — the parent can override
 * before submitting.
 */
export function suggestNickname(): string {
  return FRIENDLY_ANIMAL_NICKNAMES[0] ?? 'Otter';
}